import { prop, propOr } from 'ramda';

import { compact } from '../../../helpers/compact';
import { Document, DocumentType } from '../../../types/document';
import slicesFromProp from '../fields/slices';
import {
  footerLink,
  iconCardsContainer,
  multipleCardsContainer,
  rgpdLink,
  simpleCardContainer,
  socialNetwork,
} from '../slices/footer';
import { FooterLink } from '../slices/footer/footerLink';
import { IconCardsContainer } from '../slices/footer/iconCardsContainer';
import { MultipleCardsContainer } from '../slices/footer/multipleCardsContainer';
import { SimpleCardContainer } from '../slices/footer/simpleCardContainer';
import { SocialNetwork } from '../slices/footer/socialNetwork';
import { documentEntity } from './internal';

export enum FooterLinkTypes {
  FOOTER_LINK = 'footerLink',
  RGPD_LINK = 'rgpdLink',
}

export enum SocialLinkTypes {
  FACEBOOK = 'Facebook',
  INSTAGRAM = 'Instagram',
  LINKEDIN = 'LinkedIn',
  TWITTER = 'Twitter',
  YOUTUBE = 'Youtube',
}

export const FooterCardType = {
  SIMPLE: 'simpleCardContainer',
  MULTIPLE: 'multipleCardsContainer',
  ICON: 'iconCardsContainer',
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const processMenu = (data: Record<string, any>): FooterLink[] =>
  slicesFromProp(
    {
      footerLink: footerLink,
      rgpdLink: rgpdLink,
    },
    'menu_items',
    data
  );

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const processSocialNetworks = (data: Record<string, any>): SocialNetwork[] =>
  slicesFromProp(
    {
      Facebook: socialNetwork(SocialLinkTypes.FACEBOOK),
      Instagram: socialNetwork(SocialLinkTypes.INSTAGRAM),
      LinkedIn: socialNetwork(SocialLinkTypes.LINKEDIN),
      Twitter: socialNetwork(SocialLinkTypes.TWITTER),
      Youtube: socialNetwork(SocialLinkTypes.YOUTUBE),
    },
    'socialnetworks_list',
    data
  );

const footerCardParsers = {
  simpleCardContainer: simpleCardContainer,
  multipleCardsContainer: multipleCardsContainer,
  iconCardsContainer: iconCardsContainer,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const processFooterCards = (propName: string, data: Record<string, any>) => {
  const slices = prop(propName, data);
  if (slices && slices.length > 0) {
    const parser = propOr(() => null, slices[0].slice_type, footerCardParsers);
    return parser(slices[0]);
  }
  return {};
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const processFooter = (data: Record<string, any>): FooterType => {
  return compact({
    uid: DocumentType.FOOTER,
    type: DocumentType.FOOTER,
    menu: processMenu(data),
    cards: {
      left: processFooterCards('left_content', data),
      right: processFooterCards('right_content', data),
    },
    socialNetworks: processSocialNetworks(data),
  });
};

export const Footer = documentEntity<FooterType>(DocumentType.FOOTER, {
  processStrategy: (doc) => processFooter(prop('data', doc)),
});

export type FooterCards = {
  left: SimpleCardContainer | MultipleCardsContainer | IconCardsContainer;
  right: SimpleCardContainer | MultipleCardsContainer | IconCardsContainer;
};
export type FooterType = Document & {
  menu: FooterLink[];
  cards: FooterCards;
  socialNetworks: SocialNetwork[];
};
